import { useEffect, useMemo } from 'react';
import { Box } from '@mui/material';

import { SCROLL_DOWN, useScrollDirection } from '@widget/toolbox/hooks/useScrollDirection';
import { useCtaVisible } from '@widget/core/GAEventContext';

import type { ReactNode, FC } from 'react';

export type OverlayCmpProps = {
  action?: string;
  children: ReactNode | ReactNode[];
  showWhenListenerIsInactive?: boolean;
  overlayHeight?: number;
};

const OFFSET_Y = 250;
const OVERLAY_HEIGHT = 90;

export const OverlayCmp: FC<OverlayCmpProps> = ({
  children,
  action = 'openWidgetModal',
  showWhenListenerIsInactive = false,
  overlayHeight = OVERLAY_HEIGHT,
}) => {
  const { increaseCtaVisible } = useCtaVisible();

  useEffect(() => {
    if (increaseCtaVisible && action === 'openWidgetModal') {
      increaseCtaVisible();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  const scrollObject = useScrollDirection({
    initialDirection: SCROLL_DOWN,
    offsetY: OFFSET_Y,
    disabled: false,
  });

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  let visible = false;
  if (!showWhenListenerIsInactive) {
    visible = scrollObject.listenerActive && scrollObject.direction === 'down';
  } else {
    visible = true;
  }

  const bottom = useMemo(() => (visible ? 0 : `-${overlayHeight}px`), [visible]);

  useEffect(() => {
    if (visible) {
      document.body.style.paddingBottom = '90px';
    } else {
      document.body.style.paddingBottom = '';
    }
  }, [visible]);

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        display: {
          xs: visible ? 'block' : 'none',
          md: visible ? 'flex' : 'none',
        },
        justifyContent: 'flex-end',
        boxShadow: '0 -5px 5px -5px rgba(0, 0, 0, 0.25)',
        bottom,
        transition: 'all 300ms',
        width: '100%',
        zIndex: 9990,
        position: 'fixed',
        left: '0',
        right: '0',
        padding: '20px',
      }}
      height={`${overlayHeight}px`}
    >
      {children}
    </Box>
  );
};
